import { Modal } from '../../../components/modal/modal';
import { useContext, useEffect, useState } from 'react';
import { Button } from '../../../components/form/button';
import { Order } from '../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import './status-transitions.scss';
import { ValidationError } from '../../../components/validation-error/validation-error';
import classNames from 'classnames';
import { displayMoneyFormat } from '../../../util/util';
import { PaymentTotalSelector } from './payment-total-selector';
import { Payment, PaymentMethodType, StripeCard } from '../../../clients/quipli-service/payment/payment';
import { TerminalSelect } from '../../payment/stripe/terminal-component/terminal-select';
import { Toggle } from '../../../components/form/toggle';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useNavigate } from 'react-router-dom';
import { TenantContext } from '../../../context/tenant-context';
import { usePaymentApiV1 } from '../../../clients/quipli-service/payment/payment-api-v1';

export const CapturePayment = ({
  orderId,
  onCancel,
  onComplete,
  setCard,
  card,
  isTerminal = false,
}: {
  orderId: number;
  onCancel: () => void;
  onComplete: () => void;
  setCard: () => void;
  card?: StripeCard;
  isTerminal?: boolean;
}) => {
  const { getOrderById } = useOrderApiV1();
  const { createPayment, captureCard } = usePaymentApiV2();
  const { addTerminalCard } = usePaymentApiV1();
  const { updateOrderPayment } = useAPI();
  const { contextPath } = useContext(TenantContext);
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>();
  const [invoice, setInvoice] = useState<Invoice | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [amount, setAmount] = useState<string>();
  const [percent, setPercent] = useState<number>();
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [showAmount, setShowAmount] = useState<boolean>(true);
  const [showFacilitator, setShowFacilitator] = useState<boolean>(false);
  const [payment, setPayment] = useState<Payment>();
  const [method, setMethod] = useState<'pay' | 'setup'>();
  const [setupIntent, setSetupIntent] = useState<string>();

  useEffect(() => {
    getOrderById(orderId)
      .then((_order) => {
        setOrder(_order);
      })
      .catch((e) => toast(e.message, 'error'));
  }, []);

  useEffect(() => {
    if (order) {
      setInvoice(order.invoices.find((inv) => inv));
    }
    if (card?.confirmationTokenId) {
      setPaymentMethod({ type: PaymentMethodType.Stripe, confirmationTokenId: card.confirmationTokenId });
    }
  }, [order]);

  const createOrderCard = async () => {
    try {
      const _card = await captureCard({ confirmationTokenId: card!.confirmationTokenId!, userId: order!.user.id });
      await updateOrderPayment(orderId, { cardId: _card.id, type: PaymentMethodType.Stripe });
      toast('Payment updated successfully.');
      navigate(`${contextPath}manager/orders/view/${orderId}`);
    } catch (ex: any) {
      toast(typeof ex.message === 'string' ? ex.message : 'Something went wrong.', 'error');
    }
  };

  async function postPayment(payments?: Payment[]) {
    setShowFacilitator(false);
    setIsCapturing(false);
    if (payments && payments.length > 0) {
      const payment = payments[0];
      if (payment.gatewayStatus === 'requires_action') {
        toast('Payment requires authentication', 'error');
      } else if (payment.status === 'failed') {
        toast('Payment failed', 'error');
        return;
      }
    }
    onComplete();
  }

  async function doCapture() {
    setIsCapturing(true);
    try {
      if (invoice && amount && parseFloat(amount) > 0 && paymentMethod) {
        const payment = await createPayment({
          invoiceIds: [invoice.id],
          amount: parseFloat(amount),
          paymentMethod: paymentMethod,
          userId: invoice.customer.id,
        });

        if (!payment) {
          toast('Payment failed', 'error');
          onCancel();
        } else if (isTerminal) {
          setPayment(payment);
          setMethod('pay');
          setShowFacilitator(true);
          return;
        } else {
          if (payment.gatewayStatus === 'requires_action') {
            toast('Payment requires authentication', 'error');
            onCancel();
          } else if (payment.status === 'failed') {
            toast('Payment failed', 'error');
            onCancel();
          }
          onComplete();
        }
      }
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setIsCapturing(false);
    }
  }

  const numericAmount = amount !== undefined && parseFloat(amount);
  const paymentGreaterThanBalance = !!numericAmount && invoice && numericAmount > invoice.balance;
  const isValid = !paymentGreaterThanBalance;
  const hasPaymentsPendingAuthentication = invoice?.payments.some((p) => p.gatewayStatus === 'requires_action');
  const displayWarnings = !isValid || hasPaymentsPendingAuthentication;

  async function getShellCard() {
    if (isTerminal && paymentMethod?.type == PaymentMethodType.Stripe && order) {
      setMethod('setup');
      const { setupIntentId: _setupIntent } = await addTerminalCard(
        order.user.id,
        order.locationId,
        paymentMethod.terminalId,
        order.id,
      );
      setSetupIntent(_setupIntent);
      setShowFacilitator(true);
    }
  }

  return (
    <Modal
      isOpen={true}
      title="Capture Payment"
      actions={
        !order || isCapturing ? null : (
          <>
            {!showAmount && (
              <Button
                className="button button-secondary"
                onClick={async () => {
                  if (isTerminal && paymentMethod?.type == PaymentMethodType.Stripe) {
                    await getShellCard();
                  } else {
                    await createOrderCard();
                  }
                }}
              >
                Save Card Only
              </Button>
            )}
            {showAmount && (
              <Button
                dataCy="capture-payment-modal"
                className="button button-yellow"
                disabled={!isValid}
                onClick={() => doCapture()}
              >
                Capture Payment
              </Button>
            )}
          </>
        )
      }
      close={() => {
        !isTerminal && setCard();
        onCancel();
      }}
    >
      {!order || (isCapturing && !isTerminal) ? (
        <Spinner />
      ) : (
        <div className="flex flex-column w-full">
          {invoice && invoice.balance > 0 ? (
            <div>
              <div className="flex gap-2 text-[0.9rem]">
                <Toggle
                  onClick={() => {
                    setShowAmount((prev) => !prev);
                  }}
                  checked={showAmount}
                />
                Capture Payment
              </div>
              {showAmount && (
                <PaymentTotalSelector
                  paymentMethodType={paymentMethod?.type}
                  total={invoice.total}
                  paymentTotal={invoice.total - invoice.balance}
                  amount={amount}
                  setAmount={setAmount}
                  setPercent={setPercent}
                  percent={percent}
                  defaultPercentage={100}
                />
              )}

              {isTerminal && (
                <TerminalSelect
                  user={invoice.customer}
                  locationId={invoice.locationId}
                  setCardId={() => {}}
                  close={onCancel}
                  onComplete={postPayment}
                  onCancel={onCancel}
                  method={method}
                  setMethod={setMethod}
                  payments={[...(payment ? [payment] : [])]}
                  setPayments={(payments) => setPayment(payments[0])}
                  showFacilitator={showFacilitator}
                  setPaymentMethod={setPaymentMethod}
                  setupIntent={setupIntent}
                />
              )}
              <ValidationError className={classNames({ hidden: !displayWarnings })} style={{ maxWidth: '342px' }}>
                <ul>
                  {paymentGreaterThanBalance && (
                    <li>Cannot collect more than the order balance of {displayMoneyFormat(invoice.balance, 'usd')}</li>
                  )}
                  {invoice?.payments.some((p) => p.gatewayStatus === 'requires_action') && (
                    <li>Existing payments require authentication</li>
                  )}
                </ul>
              </ValidationError>
            </div>
          ) : (
            <div>Order has been fully paid</div>
          )}
        </div>
      )}
    </Modal>
  );
};
